import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDashboardBranchPerformance } from '@/api/dashboard'
import { pushNotification, useAppDispatch } from '@/store'
import { PnLMetricCardModel } from '@/types/clarity'
import {
  DashboardBranchPerformanceChartItemModel,
  DashboardBranchPerformanceItemModel,
} from '@/types/dashboard'
import { areFilterParamsValid } from '@/utils/filterParams'
import type {
  DashboardBranchPerformanceBranchItemResponseModel,
  DashboardBranchPerformanceDateItemResponseModel,
} from '@/types'

/** Build PnLMetricCardModel for chart: stack uses amount (sales, cogsValue, storeLaborValue), tooltip shows sales as money, cogs/storeLabor as percentage. */
function chartItemToMetricCard(
  item: DashboardBranchPerformanceChartItemModel
): PnLMetricCardModel {
  return {
    netSales: {
      value: item.sales,
      mtd: 0,
      ytd: 0,
      metricType: 'money',
      amount: item.sales,
    },
    cogs: {
      value: item.cogs,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.cogsValue,
    },
    storeLabor: {
      value: item.storeLabor,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.storeLaborValue,
    },
  }
}

function mapDateItemToChartModel(
  item: DashboardBranchPerformanceDateItemResponseModel
): DashboardBranchPerformanceChartItemModel {
  return {
    label: item.date,
    sales: item.sales,
    salesChange: item.sales_change,
    cogs: item.cogs,
    cogsChange: item.cogs_change,
    cogsValue: item.cogs_value,
    storeLabor: item.store_labor,
    storeLaborChange: item.store_labor_change,
    storeLaborValue: item.store_labor_value,
  }
}

/** Map old branch format to chart model; derive cogsValue/storeLaborValue from sales and percentages when not provided. */
function mapOldBranchItemToChartModel(
  item: DashboardBranchPerformanceBranchItemResponseModel
): DashboardBranchPerformanceChartItemModel {
  const sales = item.sales ?? 0
  return {
    label: item.branch_name,
    sales,
    salesChange: item.sales_change ?? 0,
    cogs: item.cogs,
    cogsChange: item.cogs_change ?? 0,
    cogsValue: sales * (item.cogs / 100),
    storeLabor: item.store_labor,
    storeLaborChange: item.store_labor_change ?? 0,
    storeLaborValue: sales * (item.store_labor / 100),
  }
}

export default function useDashboardBranchPerformance(
  branches: string,
  startDate: string,
  endDate: string,
  chartType: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areFilterParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [branchPerformanceChartData, setBranchPerformanceChartData] = useState<
    DashboardBranchPerformanceChartItemModel[]
  >([])
  const [systemTotalPerformance, setSystemTotalPerformance] = useState<
    DashboardBranchPerformanceItemModel | undefined
  >()
  const [metricCardRecord, setMetricCardRecord] = useState<
    Record<string, PnLMetricCardModel>
  >({})
  const [miniCardData, setMiniCardData] = useState<
    PnLMetricCardModel | undefined
  >()
  const [trendData, setTrendData] = useState<PnLMetricCardModel | undefined>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const response = await getDashboardBranchPerformance(
        branches,
        startDate,
        endDate,
        chartType,
        'custom'
      )

      if ('date_data' in response && response.date_data) {
        const dateData = response.date_data
        const chartItems = dateData.map(mapDateItemToChartModel)
        setBranchPerformanceChartData(chartItems)

        const record: Record<string, PnLMetricCardModel> = {}
        for (const item of chartItems) {
          record[item.label] = chartItemToMetricCard(item)
        }
        setMetricCardRecord(record)
        setSystemTotalPerformance(undefined)
        setMiniCardData(undefined)
        setTrendData(undefined)
        return
      }

      if ('branch_data' in response && response.branch_data) {
        const branchData = response.branch_data
        const chartItems = branchData.map(mapOldBranchItemToChartModel)
        setBranchPerformanceChartData(chartItems)

        const systemTotalItem = branchData.find(
          (item) => item.branch_name === 'system_total'
        )
        const branchItems = chartItems.filter(
          (item) => item.label !== 'system_total'
        )

        const record: Record<string, PnLMetricCardModel> = {}
        for (const item of branchItems) {
          record[item.label] = chartItemToMetricCard(item)
        }
        setMetricCardRecord(record)

        if (systemTotalItem) {
          const systemChart = mapOldBranchItemToChartModel(systemTotalItem)
          setSystemTotalPerformance({
            branchName: systemTotalItem.branch_name,
            value: systemTotalItem.value,
            storeProfits: systemTotalItem.store_profits,
            cogs: systemTotalItem.cogs,
            storeLabor: systemTotalItem.store_labor,
            opex: systemTotalItem.opex,
            occupancy: systemTotalItem.occupancy,
            sales: systemTotalItem.sales,
            salesChange: systemTotalItem.sales_change,
            valueChange: systemTotalItem.value_change,
            storeProfitsChange: systemTotalItem.store_profits_change,
            cogsChange: systemTotalItem.cogs_change,
            storeLaborChange: systemTotalItem.store_labor_change,
            opexChange: systemTotalItem.opex_change,
            occupancyChange: systemTotalItem.occupancy_change,
          })
          setMiniCardData(chartItemToMetricCard(systemChart))
          setTrendData({
            netSales: {
              value: (systemTotalItem.sales ?? 0) - (systemTotalItem.sales_change ?? 0),
              mtd: 0,
              ytd: 0,
              metricType: 'money',
            },
            cogs: {
              value: systemTotalItem.cogs - (systemTotalItem.cogs_change ?? 0),
              mtd: 0,
              ytd: 0,
              metricType: 'percentage',
            },
            storeLabor: {
              value:
                systemTotalItem.store_labor - (systemTotalItem.store_labor_change ?? 0),
              mtd: 0,
              ytd: 0,
              metricType: 'percentage',
            },
          })
        } else {
          setSystemTotalPerformance(undefined)
          setMiniCardData(undefined)
          setTrendData(undefined)
        }
        return
      }

      setBranchPerformanceChartData([])
      setMetricCardRecord({})
      setSystemTotalPerformance(undefined)
      setMiniCardData(undefined)
      setTrendData(undefined)
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, dispatch, t, branches, startDate, endDate, chartType])

  const refetch = useCallback(() => {
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    branchPerformanceChartData,
    systemTotalPerformance,
    metricCardRecord,
    miniCardData,
    trendData,
    loading,
    refetch,
  }
}
